import { useEffect, useRef, useState } from "react";
import {
  ArrowPathIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTranslations } from "use-intl";
import {
  MessageAttachments,
  PendingAttachment,
  UploadingAttachment,
} from "@/app/Threads/hooks/useMessageAttachments";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { cn } from "@/lib/utils";

const MAX_AUTO_GROW_ROWS = 10;

function useAutoGrow() {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const appliedHeightRef = useRef<number | null>(null);
  const manuallyResizedRef = useRef(false);

  function applyHeight(height: string) {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = height;
    appliedHeightRef.current = el.offsetHeight;
  }

  function grow() {
    const el = textareaRef.current;
    if (!el || manuallyResizedRef.current) {
      return;
    }

    const styles = getComputedStyle(el);
    const borders =
      parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    const maxHeight =
      parseFloat(styles.lineHeight) * MAX_AUTO_GROW_ROWS +
      parseFloat(styles.paddingTop) +
      parseFloat(styles.paddingBottom) +
      borders;

    el.style.height = "auto";
    applyHeight(`${Math.min(el.scrollHeight + borders, maxHeight)}px`);
  }

  function reset() {
    if (manuallyResizedRef.current) {
      return;
    }
    applyHeight("");
  }

  function ref(el: HTMLTextAreaElement | null) {
    textareaRef.current = el;
    if (!el) {
      return;
    }

    grow();

    // The resize handle writes an inline height we never asked for; that is the
    // only signal that the user picked their own size.
    const observer = new ResizeObserver(() => {
      if (el.offsetHeight !== appliedHeightRef.current) {
        manuallyResizedRef.current = true;
      }
    });
    observer.observe(el);

    return () => observer.disconnect();
  }

  return { ref, grow, reset };
}

export type ComposeMode = "message" | "note";

type Props = {
  onSend: (body: string, filePaths: string[], mode: ComposeMode) => void;
  onTyping?: () => void;
  attachments: MessageAttachments;
  autoFocus?: boolean;
  onFocus?: () => void;
  className?: string;
  allowNotes?: boolean;
};

export function MessageInput({
  onSend,
  onTyping,
  attachments,
  autoFocus = true,
  onFocus,
  className,
  allowNotes = true,
}: Props) {
  const t = useTranslations("thread.compose");
  const { isAdmin } = useTenant();
  const [mode, setMode] = useState<ComposeMode>("message");
  const [text, setText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoGrow = useAutoGrow();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const trimmed = text.trim();
    if (!trimmed && attachments.pending.length === 0) {
      return;
    }

    onSend(trimmed, attachments.filePaths, mode);
    setText("");
    attachments.clear();
    // Notes are chosen per send: a reply meant for the supplier must never
    // inherit note mode from the one before it.
    setMode("message");
    autoGrow.reset();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files) {
      return;
    }

    const selected = Array.from(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    await attachments.add(selected);
  }

  const showPreviews =
    attachments.uploading.length > 0 || attachments.pending.length > 0;
  const isNote = mode === "note";

  return (
    <form
      onSubmit={handleSubmit}
      className={cn("border-t border-gray-200 p-4", className)}
    >
      {isAdmin && allowNotes ? (
        <ComposeModeToggle mode={mode} onChange={setMode} />
      ) : null}

      {showPreviews ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {attachments.uploading.map((file) => (
            <UploadingFilePreview key={file.id} file={file} />
          ))}
          {attachments.pending.map((file) => (
            <PendingFilePreview
              key={file.id}
              file={file}
              onRemove={() => attachments.remove(file.id)}
            />
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          multiple
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          disabled={attachments.isUploading}
        >
          <PaperClipIcon className="h-5 w-5" />
        </button>

        <textarea
          ref={autoGrow.ref}
          autoFocus={autoFocus}
          onFocus={onFocus}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            autoGrow.grow();
            // A note is for Imoova alone: typing one must not tell the
            // supplier or driver that something is being written.
            if (!isNote) {
              onTyping?.();
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={isNote ? t("notePlaceholder") : t("messagePlaceholder")}
          rows={1}
          className={cn(
            "min-h-9 flex-1 resize-y overflow-y-auto rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1",
            isNote
              ? "border-amber-200 bg-amber-100 text-gray-900 placeholder:text-gray-600 focus:border-amber-300 focus:ring-amber-300"
              : "border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500",
          )}
        />

        <button
          type="submit"
          disabled={
            attachments.isUploading ||
            (text.trim() === "" && attachments.pending.length === 0)
          }
          className={cn(
            "rounded-lg p-2 transition-colors disabled:opacity-50",
            isNote
              ? "bg-amber-300 text-amber-900 hover:bg-amber-400"
              : "bg-blue-600 text-white hover:bg-blue-700",
          )}
        >
          <PaperAirplaneIcon className="h-5 w-5" />
        </button>
      </div>
    </form>
  );
}

function ComposeModeToggle({
  mode,
  onChange,
}: {
  mode: ComposeMode;
  onChange: (mode: ComposeMode) => void;
}) {
  const t = useTranslations("thread.compose");

  return (
    <div className="mb-2 inline-flex rounded-lg bg-gray-100 p-0.5">
      <ComposeModeButton
        isActive={mode === "message"}
        activeClassName="bg-white text-blue-700 shadow-sm"
        onClick={() => onChange("message")}
      >
        {t("messageMode")}
      </ComposeModeButton>
      <ComposeModeButton
        isActive={mode === "note"}
        activeClassName="bg-amber-200 text-amber-900 shadow-sm"
        onClick={() => onChange("note")}
      >
        <LockClosedIcon className="size-3.5" />
        {t("noteMode")}
      </ComposeModeButton>
    </div>
  );
}

function ComposeModeButton({
  isActive,
  activeClassName,
  onClick,
  children,
}: {
  isActive: boolean;
  activeClassName: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
        isActive ? activeClassName : "text-gray-500 hover:text-gray-700",
      )}
    >
      {children}
    </button>
  );
}

function UploadingFilePreview({ file }: { file: UploadingAttachment }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file.file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file.file]);

  if (previewUrl) {
    return (
      <div className="relative">
        <img
          src={previewUrl}
          alt={file.file.name}
          className="h-20 w-20 rounded-lg object-cover opacity-50"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-700" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs text-gray-400">
      <ArrowPathIcon className="h-3 w-3 animate-spin" />
      <span className="max-w-32 truncate">{file.file.name}</span>
    </div>
  );
}

function PendingFilePreview({
  file,
  onRemove,
}: {
  file: PendingAttachment;
  onRemove: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file.file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file.file]);

  if (previewUrl) {
    return (
      <div className="relative">
        <img
          src={previewUrl}
          alt={file.file.name}
          className="h-20 w-20 rounded-lg object-cover"
        />
        <button
          type="button"
          onClick={onRemove}
          className="absolute -right-1 -top-1 rounded-full bg-gray-800 p-0.5 text-white hover:bg-gray-700"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
      <PaperClipIcon className="h-3 w-3" />
      <span className="max-w-32 truncate">{file.file.name}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 text-gray-400 hover:text-gray-600"
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
    </div>
  );
}
